import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  Menu,
  Snackbar,
  Alert,
  AlertTitle,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExcelViewerDialog from './GLSync/ExcelViewerDialog';
import ViewDataDialog from './GLSync/ViewDataDialog';
import { useUserRights } from '../../context/UserRightsContext';
import { formatDate, formatDateTime, initializeFormats, getFormatConfig } from '../../utils/global';
import { AuditLoggers } from '../../utils/auditLogger';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateGridActionIconSx,
  harmonatePlainIconButtonSx,
} from '../../theme/harmonateStyles';
import {
  GL_SYNC_TRIAL_BALANCE_DOC_TYPE,
  getDocumentTypeDisplayName,
  isGlSyncUploadDocumentType,
  stripGlSystemLabel,
} from '../../constants/glSyncDocumentTypes';

/** Ref-template id persisted on mapping header (camelCase or snake_case). */
const getMappingTemplateId = (mapping) => {
  const raw =
    mapping?.templateId ??
    mapping?.refTemplateId ??
    mapping?.template_id ??
    mapping?.ref_template_id ??
    mapping?.glSyncTemplateId ??
    mapping?.gl_sync_template_id ??
    null;
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
};

const normGlName = (value) =>
  stripGlSystemLabel(value).replace(/\s+/g, ' ').trim().toUpperCase();

/** Non-empty template label already on mapping-headers row (skip ref-template fetch for that card). */
const getExplicitTemplateNameOnHeader = (m) =>
  String(
    m?.templateName ??
      m?.template_name ??
      m?.refTemplateName ??
      m?.ref_template_name ??
      m?.glSyncTemplateName ??
      m?.gl_sync_template_name ??
      m?.refTemplate?.templateName ??
      m?.ref_template?.template_name ??
      m?.refTemplate?.name ??
      ''
  ).trim();

const GLSync = () => {
  const navigate = useNavigate();
  const [selectedSource, setSelectedSource] = useState('Efront');
  const [selectedDestination, setSelectedDestination] = useState('Allvue');
  const [cardMenuAnchorEl, setCardMenuAnchorEl] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [glMappings, setGlMappings] = useState([]);
  /** templateName resolved via `ref-templates/by-document-and-gl` when header has templateId (same as GLSyncGLMappingWorkflow). */
  const [refTemplateNamesByMappingId, setRefTemplateNamesByMappingId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  /** When false, Uploads grid shows Trial Balance files only; true shows all GL Sync types. */
  const [showSubDocs, setShowSubDocs] = useState(false);
  const [uploadsHeaderMenuAnchor, setUploadsHeaderMenuAnchor] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedUploadRow, setSelectedUploadRow] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewDataOpen, setViewDataOpen] = useState(false);
  const [viewDataData, setViewDataData] = useState(null);
  const [viewDataLoading, setViewDataLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null); // 'card' or 'upload'
  const [itemToDelete, setItemToDelete] = useState(null);

  /** Document details + audit logs modal (Workflow / Bank Reference parity) */
  const [auditLogs, setAuditLogs] = useState([]);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [auditLogsPaginationModel, setAuditLogsPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  /** Upload row used for WorkflowDrawer-parity actions (Snowflake / Raw JSON) while logs modal is open */
  const [logsModalSourceRow, setLogsModalSourceRow] = useState(null);
  const [logsJsonDialogOpen, setLogsJsonDialogOpen] = useState(false);
  const [logsJsonLoading, setLogsJsonLoading] = useState(false);
  const [logsJsonError, setLogsJsonError] = useState(null);
  const [logsJsonData, setLogsJsonData] = useState(null);
  const [logsExtractionDialogOpen, setLogsExtractionDialogOpen] = useState(false);
  const [logsExtractionLoading, setLogsExtractionLoading] = useState(false);
  const [logsExtractionDetails, setLogsExtractionDetails] = useState(null);

  // Add these state declarations with the other useState hooks
  const [workflowMenuAnchor, setWorkflowMenuAnchor] = useState(null);

  const { hasPermission, isHarmonateUser } = useUserRights();

  const [dateTimeFormat, setDateTimeFormat] = useState('DD/MM/YYYY hh:mm:ss a');
  // Initialize formats when component mounts
  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.DateTimeFormat) {
          setDateTimeFormat(config.DateTimeFormat);
        }
      });
    }
  }, []);

  // Add these handler functions before the return statement
  const handleWorkflowMenuOpen = (event) => {
    setWorkflowMenuAnchor(event.currentTarget);
  };

  const handleWorkflowMenuClose = () => {
    setWorkflowMenuAnchor(null);
  };

  const handleAccountsClick = () => {
    handleWorkflowMenuClose();
    navigate('/gl-accounts');
  };

  const handleDeleteClick = () => {
    handleWorkflowMenuClose();
    console.log('Delete clicked');
    // Add your delete logic here
  };

  // Pagination state for cards
  const [currentCardPage, setCurrentCardPage] = useState(0);
  const cardsPerPage = 4;

  // Pagination state for recent uploads DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const isTrialBalanceUploadRow = (row) => {
    const docType = String(row?.documentType || '').trim().toLowerCase();
    return docType.includes('trial balance');
  };

  const visibleUploads = useMemo(() => {
    if (showSubDocs) return recentUploads;
    return recentUploads.filter(isTrialBalanceUploadRow);
  }, [recentUploads, showSubDocs]);

  // Recent uploads: same pattern as GLAccounts — parallel file-instances + gl-headers; grid shows header rows only (empty if no headers)
  const fetchRecentUploads = async () => {
    setLoadingUploads(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        console.error('Missing token or tenant ID');
        return;
      }

      const docTypeResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=GL`,
        {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        }
      );

      if (!docTypeResponse.ok) {
        throw new Error('Failed to fetch GL document types');
      }

      const docTypeData = await docTypeResponse.json();

      if (docTypeData.length === 0) {
        console.log('No GL document types found');
        setRecentUploads([]);
        return;
      }

      const glSyncDocTypes = docTypeData.filter((d) =>
        isGlSyncUploadDocumentType(getDocumentTypeDisplayName(d))
      );
      if (glSyncDocTypes.length === 0) {
        console.log('No GL Sync upload document types found in GL category list');
        setRecentUploads([]);
        return;
      }

      const authHeaders = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const glHeadersUrl = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-headers/by-tenant`);
      glHeadersUrl.searchParams.set('tenant_id', tenantId);

      const fileInstanceResponses = await Promise.all(
        glSyncDocTypes.map((dt) => {
          const docTypeId = dt.documentTypeId || dt.id;
          return fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/by-document-type/${docTypeId}?tenantId=${tenantId}`,
            { headers: authHeaders }
          );
        })
      );
      const glHeadersResponse = await fetch(glHeadersUrl.toString(), { headers: authHeaders });

      const fileLists = await Promise.all(
        fileInstanceResponses.map(async (res) => {
          if (!res.ok) return [];
          const data = await res.json().catch(() => []);
          return Array.isArray(data) ? data : [];
        })
      );
      const fileList = fileLists.flat();

      let headersList = [];
      if (glHeadersResponse.ok) {
        const rawHeaders = await glHeadersResponse.json();
        const allHeaders = Array.isArray(rawHeaders) ? rawHeaders : [];
        headersList = allHeaders.filter((header) =>
          isGlSyncUploadDocumentType(header.documentType)
        );
      } else {
        console.warn('gl-headers request failed:', glHeadersResponse.status);
      }

      const findFileInstanceForHeader = (header) => {
        const docId = header.documentId;
        const fileName = (header.fileName || '').trim();
        if (docId) {
          const byDoc = fileList.find(
            (f) => f.id === docId || f.integrationId === docId
          );
          if (byDoc) return byDoc;
        }
        if (fileName) {
          return fileList.find((f) => (f.name || '').trim() === fileName);
        }
        return undefined;
      };

      let transformedData;
      if (headersList.length > 0) {
        const sorted = [...headersList].sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
        transformedData = sorted.map((header, index) => {
          const fi = findFileInstanceForHeader(header);
          const sourceFromFi = stripGlSystemLabel(fi?.sourceGeneralLedger);
          const destFromFi = stripGlSystemLabel(fi?.destinationGeneralLedger);
          const targetFromHeader =
            header.targetGlSystem != null && String(header.targetGlSystem).trim() !== ''
              ? stripGlSystemLabel(header.targetGlSystem) || String(header.targetGlSystem).trim()
              : '';
          return {
            id: header.glHeaderId || `gl-header-${index}`,
            name: header.fileName || 'Unknown File',
            source: sourceFromFi || header.glSystem || 'Unknown',
            surrogateKey: header.surrogateKey ?? header.surrogate_key ?? '—',
            targetGlSystem: targetFromHeader || '—',
            destination: targetFromHeader || destFromFi || '-',
            date: header.createdAt
              ? formatDateTime(header.createdAt, dateTimeFormat)
              : 'Unknown Date',
            integrationId: fi?.integrationId ?? header.documentId,
            unitOfWorkId: fi?.unitOfWorkId,
            fileInstanceId: fi?.id ?? null,
            fastApiFileInstanceId: fi?.integrationId ?? fi?.fastApiFileInstanceId ?? null,
            processId: fi?.processId ?? null,
            glHeaderId: header.glHeaderId,
            documentId: header.documentId,
            documentType: header.documentType,
            glVersion: header.glVersion ?? '-',
            fundId: header.fundId ?? header.fund_id ?? null,
            fundCode: header.fundCode ?? header.fund_code ?? null,
            fundName: header.fundName ?? header.fund_name ?? null,
          };
        });
      } else {
        // Keep file-instances fetch (used for merging when headers exist); do not backfill grid from it
        transformedData = [];
      }

      setRecentUploads(transformedData);
    } catch (error) {
      console.error('Error fetching recent uploads:', error);
    } finally {
      setLoadingUploads(false);
    }
  };



  // useEffect(() => {
  //   const fetchGlMappings = async () => {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');

  //     if (!token) {
  //       setError('No authentication token found');
  //       setLoading(false);
  //       return;
  //     }

  //     if (!tenantId) {
  //       setError('No tenant ID found. Please ensure you are properly authenticated.');
  //       setLoading(false);
  //       return;
  //     }

  //     try {
  //       const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/mapping-headers?tenant=${tenantId}`, {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //         },
  //       });

  //       if (!response.ok) {
  //         const errorData = await response.json();
  //         throw new Error(errorData.detail || 'Failed to fetch GL mapping headers');
  //       }

  //       const data = await response.json();
  //       setGlMappings(data);
  //     } catch (error) {
  //       console.error('Error fetching GL mapping headers:', error);
  //       setError(`Error fetching GL mapping headers: ${error.message}`);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchGlMappings();
  //   fetchRecentUploads();
  // }, []);

  // Get unique source and destination combinations from the API data
  // useEffect(() => {
  //   const fetchGlMappings = async () => {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');

  //     if (!token) {
  //       setError('No authentication token found');
  //       setLoading(false);
  //       return;
  //     }

  //     if (!tenantId) {
  //       setError('No tenant ID found. Please ensure you are properly authenticated.');
  //       setLoading(false);
  //       return;
  //     }

  //     try {
  //       const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/mapping-headers?tenant=${tenantId}`, {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //         },
  //       });

  //       if (!response.ok) {
  //         const errorData = await response.json();
  //         throw new Error(errorData.detail || 'Failed to fetch GL mapping headers');
  //       }

  //       const data = await response.json();
  //       setGlMappings(data);
  //     } catch (error) {
  //       console.error('Error fetching GL mapping headers:', error);
  //       setError(`Error fetching GL mapping headers: ${error.message}`);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchGlMappings();
  // }, []);
  // Replace the existing useEffect hooks with these updated versions:

  useEffect(() => {
    const fetchGlMappings = async () => {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      if (!tenantId) {
        setError('No tenant ID found. Please ensure you are properly authenticated.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/mapping-headers?tenant=${tenantId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to fetch GL mapping headers');
        }

        const data = await response.json();
        setGlMappings(data);
        setCurrentCardPage(0); // Reset to first page when data is fetched
      } catch (error) {
        console.error('Error fetching GL mapping headers:', error);
        setError(`Error fetching GL mapping headers: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchGlMappings();
  }, []); // Empty dependency array - only fetch once on mount

  useEffect(() => {
    let cancelled = false;

    const resolveRefTemplateDisplayNames = async () => {
      if (!glMappings.length) {
        setRefTemplateNamesByMappingId({});
        return;
      }

      const needsResolution = glMappings.some((m) => !getExplicitTemplateNameOnHeader(m));

      if (!needsResolution) {
        setRefTemplateNamesByMappingId({});
        return;
      }

      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) {
        setRefTemplateNamesByMappingId({});
        return;
      }

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');

      try {
        const dtRes = await fetch(
          `${baseUrl}conductor/document-type-tenant-configs/tenant/${encodeURIComponent(tenantId)}?category=GL`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        let documentTypeId = '';
        if (dtRes.ok) {
          const dtJson = await dtRes.json().catch(() => []);
          const list = Array.isArray(dtJson) ? dtJson : [];
          const glSyncRow = list.find(
            (item) =>
              getDocumentTypeDisplayName(item) === GL_SYNC_TRIAL_BALANCE_DOC_TYPE
          );
          documentTypeId = String(glSyncRow?.documentTypeId || glSyncRow?.document_type_id || '').trim();
        }
        if (!documentTypeId || cancelled) {
          setRefTemplateNamesByMappingId({});
          return;
        }

        const sysRes = await fetch(
          `${baseUrl}conductor/tenant-systems/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!sysRes.ok || cancelled) {
          setRefTemplateNamesByMappingId({});
          return;
        }
        const sysRaw = await sysRes.json().catch(() => []);
        const systems = Array.isArray(sysRaw)
          ? sysRaw
          : Array.isArray(sysRaw?.data)
            ? sysRaw.data
            : [];

        const glSystemIdBySourceName = (sourceGlLabel) => {
          const needle = normGlName(sourceGlLabel);
          if (!needle) return null;
          const byName = systems.find((s) => normGlName(s?.name) === needle);
          if (byName?.systemId || byName?.system_id || byName?.id) {
            return String(byName.systemId || byName.system_id || byName.id);
          }
          const raw = String(sourceGlLabel ?? '').trim();
          const byId = systems.find(
            (s) =>
              String(s?.id ?? '').trim().toLowerCase() === raw.toLowerCase() ||
              String(s?.systemId ?? '').trim().toLowerCase() === raw.toLowerCase() ||
              String(s?.system_id ?? '').trim().toLowerCase() === raw.toLowerCase()
          );
          return byId?.systemId || byId?.system_id || byId?.id
            ? String(byId.systemId || byId.system_id || byId.id)
            : null;
        };

        const templatesByGlSystemId = {};

        const fetchTemplatesForGl = async (glSystemId) => {
          if (!glSystemId) return [];
          if (templatesByGlSystemId[glSystemId]) return templatesByGlSystemId[glSystemId];
          const tplRes = await fetch(
            `${baseUrl}conductor/ref-templates/by-document-and-gl?documentTypeId=${encodeURIComponent(documentTypeId)}&glSystemId=${encodeURIComponent(glSystemId)}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          if (!tplRes.ok) {
            templatesByGlSystemId[glSystemId] = [];
            return [];
          }
          const arr = await tplRes.json().catch(() => []);
          const list = Array.isArray(arr) ? arr : [];
          templatesByGlSystemId[glSystemId] = list;
          return list;
        };

        const next = {};
        for (const mapping of glMappings) {
          const mappingKey = mapping.mappingInstanceId ?? mapping.id;
          if (mappingKey == null) continue;
          if (getExplicitTemplateNameOnHeader(mapping)) continue;

          const glSysId = glSystemIdBySourceName(mapping.sourceGlSystem ?? mapping.source_gl_system);
          if (!glSysId) continue;

          const templates = await fetchTemplatesForGl(glSysId);
          if (cancelled) return;

          const templateIdOnHeader = getMappingTemplateId(mapping);
          let name = '';
          if (templateIdOnHeader) {
            const hit = templates.find(
              (t) => String(t.templateId ?? t.id ?? '').trim() === templateIdOnHeader
            );
            if (hit != null) {
              name = String(hit.templateName ?? hit.name ?? '').trim();
            }
          }
          // Match GLSyncGLMappingWorkflow default: no template id on header → first row from ref-templates API
          if (!name && templates.length > 0) {
            name = String(templates[0].templateName ?? templates[0].name ?? '').trim();
          }
          if (name) {
            next[mappingKey] = name;
          }
        }

        if (!cancelled) {
          setRefTemplateNamesByMappingId(next);
        }
      } catch (e) {
        console.warn('GLSync: resolve ref-template names:', e);
        if (!cancelled) {
          setRefTemplateNamesByMappingId({});
        }
      }
    };

    void resolveRefTemplateDisplayNames();

    return () => {
      cancelled = true;
    };
  }, [glMappings]);

  useEffect(() => {
    if (dateTimeFormat) {
      fetchRecentUploads();
    }
  }, [dateTimeFormat]);

  const getIndividualMappings = () => {
    return glMappings.map((mapping) => {
      const mappingKey = mapping.mappingInstanceId ?? mapping.id;
      const mappingDescription = String(
        mapping.mappingDescription ?? mapping.mapping_description ?? ''
      ).trim();
      const resolvedFromRefs = mappingKey != null ? String(refTemplateNamesByMappingId[mappingKey] ?? '').trim() : '';
      const explicitTemplateName = getExplicitTemplateNameOnHeader(mapping);
      const templateName = explicitTemplateName || resolvedFromRefs;
      const fundCode = mapping.fundCode ?? mapping.fund_code ?? null;
      const fundName = mapping.fundName ?? mapping.fund_name ?? null;
      const fundId = mapping.fundId ?? mapping.fund_id ?? null;
      const fundLabel = fundCode || fundName || null;
      return {
        name: fundLabel
          ? `To ${mapping.targetGlSystem} · ${fundLabel}`
          : `To ${mapping.targetGlSystem}`,
        icon: <PeopleIcon sx={{ color: ht.text }} />,
        source: mapping.sourceGlSystem,
        destination: mapping.targetGlSystem,
        fundId,
        fundCode,
        fundName,
        fundLabel,
        from_gl_code: null,
        to_gl_code: null,
        count: 1,
        id: mapping.mappingInstanceId,
        mappingDescription,
        templateName,
      };
    });
  };
  const quickMapsData = getIndividualMappings();

  // Calculate pagination for cards
  const totalPages = Math.ceil(quickMapsData.length / cardsPerPage);
  const startIndex = currentCardPage * cardsPerPage;
  const endIndex = startIndex + cardsPerPage;
  const currentCards = quickMapsData.slice(startIndex, endIndex);

  const handlePreviousCards = () => {
    setCurrentCardPage(prev => Math.max(0, prev - 1));
  };

  const handleNextCards = () => {
    setCurrentCardPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  const handleLogsModalViewRawJson = async (file) => {
    try {
      setLogsJsonLoading(true);
      setLogsJsonError(null);
      setLogsJsonData(null);
      setLogsJsonDialogOpen(true);

      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!file?.unitOfWorkId) {
        throw new Error('File does not have a unitOfWorkId');
      }
      if (!file?.fastApiFileInstanceId || !file?.processId) {
        throw new Error('File does not have integration / workflow identifiers for extracted JSON');
      }

      const s3Path = `t-${tenantId}/uow-${file.unitOfWorkId}/extracted/output/FI-${file.fastApiFileInstanceId}-WI-${file.processId}.json`;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/presigned-url-for-path` +
          `?s3Path=${encodeURIComponent(s3Path)}` +
          `&tenantId=${encodeURIComponent(tenantId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to get presigned URL: ${response.status} ${response.statusText}`
        );
      }

      const { url } = await response.json();

      const jsonResponse = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!jsonResponse.ok) {
        throw new Error('Failed to fetch extracted JSON data');
      }

      const data = await jsonResponse.json();
      setLogsJsonData({
        content: JSON.stringify(data, null, 2),
        downloadUrl: url,
        isBinary: false,
        fileName: `FI-${file.fileInstanceId ?? file.id}.json`,
      });
    } catch (error) {
      console.error('Error:', error);
      setLogsJsonError(error.message);
      setError(`Failed to load file: ${error.message}`);
    } finally {
      setLogsJsonLoading(false);
    }
  };

  const handleLogsModalSnowflakeClick = async () => {
    try {
      setLogsExtractionLoading(true);
      const token = localStorage.getItem('token');
      const tenantIntegration = localStorage.getItem('tenant_integration');

      setLogsExtractionDetails(null);

      const file = logsModalSourceRow;
      if (!file) {
        setError('No file context for extraction details');
        return;
      }

      const fileInstanceId = file.fastApiFileInstanceId;
      const workflowInstanceId = file.processId;

      if (!fileInstanceId || !workflowInstanceId) {
        setError('Could not find file instance ID / workflow instance ID on this upload row');
        return;
      }

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-extraction-json?file_instance_id=${fileInstanceId}&workflow_instance_id=${workflowInstanceId}`;
      if (tenantIntegration) {
        apiUrl += `&tenant_id=${encodeURIComponent(tenantIntegration)}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setLogsExtractionDetails(data);
      setLogsExtractionDialogOpen(true);
    } catch (error) {
      console.error('Error fetching extraction details:', error);
      setError('Failed to load extraction details');
    } finally {
      setLogsExtractionLoading(false);
    }
  };

  const handleUploadRowInfoClick = async (event, row) => {
    event.stopPropagation();
    setLogsModalSourceRow(row || null);
    /** Same id used for `file-instances/${id}/delete` — must match WorkflowDrawer `row.id`. */
    const fileInstancePk = row?.fileInstanceId ?? null;
    if (!fileInstancePk) {
      setError('No file instance is linked to this upload.');
      return;
    }
    try {
      setLogsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      // Same tenant key as `fetchRecentUploads` / WorkflowDrawer (not integration tenant).
      const tenantId = localStorage.getItem('tenant_id')?.trim();
      if (!token || !tenantId) {
        setError('Authentication required');
        return;
      }
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
      const auditLogsHref = `${baseUrl}conductor/file-instances/${encodeURIComponent(
        String(fileInstancePk)
      )}/with-audit-logs?tenantId=${encodeURIComponent(tenantId)}`;

      const response = await fetch(auditLogsHref, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const responseText = await response.text();
      if (!response.ok) {
        let detail = `Request failed (${response.status})`;
        if (responseText) {
          try {
            const errBody = JSON.parse(responseText);
            detail =
              (typeof errBody.message === 'string' && errBody.message) ||
              (typeof errBody.error === 'string' && errBody.error) ||
              (typeof errBody.detail === 'string' && errBody.detail) ||
              detail;
          } catch {
            detail = responseText.length > 200 ? `${responseText.slice(0, 200)}…` : responseText;
          }
        }
        throw new Error(detail);
      }

      let data = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseErr) {
        console.error('audit-logs JSON parse:', parseErr, responseText);
        throw new Error('Server returned invalid JSON for audit logs.');
      }

      setDocumentDetails(data.document ?? null);
      setAuditLogs(Array.isArray(data.auditLogs) ? data.auditLogs : []);
      setAuditLogsPaginationModel((p) => ({ ...p, page: 0 }));
      setIsLogsModalOpen(true);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err?.message ? String(err.message) : 'Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'documentType',
      headerName: 'Document Type',
      width: 200,
    },
    {
      field: 'source',
      headerName: 'Source GL',
      width: 150,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'targetGlSystem',
      headerName: 'Target GL',
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'glVersion',
      headerName: 'Version',
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    // {
    //   field: 'date',
    //   headerName: 'Date',
    //   width: 120,
    //   align: 'right',
    //   headerAlign: 'right'
    // },
    {
      field: 'date',
      headerName: 'Date',
      width: 180,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.875rem' }}>
          {params.value || ''}
        </Typography>
      ),
    },
    {
      field: 'detailsInfo',
      headerName: '',
      width: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.fileInstanceId ? 'View details' : 'No file instance for this upload'
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={!params.row.fileInstanceId}
              onClick={(e) => handleUploadRowInfoClick(e, params.row)}
              sx={{ color: ht.green }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          size="small"
          sx={harmonateGridActionIconSx}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleApprove = () => {
    if (!isHarmonateUser && !hasPermission('GL Sync', 'add')) return;
    console.log('Approved with Source GL:', selectedSource, 'Destination GL:', selectedDestination);
    navigate("/gl-sync/mapping");
  };

  const handleCardClick = (mapping) => {
    navigate('/gl-sync/upload-mapping', {
      state: {
        sourceGL: mapping.source,
        destinationGL: mapping.destination,
        mappingId: mapping.id,
        fundId: mapping.fundId || null,
        fundCode: mapping.fundCode || null,
        fundName: mapping.fundName || null,
      }
    });
  };

  // const handleCardMenuClick = (event, cardName) => {
  //   event.stopPropagation(); // Prevent card click
  //   setCardMenuAnchorEl(event.currentTarget);
  //   setSelectedCard(cardName);
  // };
  const handleCardMenuClick = (event, cardId) => {
    event.stopPropagation(); // Prevent card click
    setCardMenuAnchorEl(event.currentTarget);
    setSelectedCard(cardId); // Store ID instead of name
  };


  const handleCardMenuClose = () => {
    setCardMenuAnchorEl(null);
    setSelectedCard(null);
  };

  // const handleEditCard = () => {
  //   const selectedMapping = quickMapsData.find(map => map.name === selectedCard);
  //   if (selectedMapping) {
  //     navigate(`/gl-sync/edit`, {
  //       state: {
  //         mappingInstanceId: selectedMapping.id,
  //         sourceGL: selectedMapping.source,
  //         destinationGL: selectedMapping.destination
  //       }
  //     });
  //   }
  //   handleCardMenuClose();
  // };
  // const handleEditCard = () => {
  //   const selectedMapping = quickMapsData.find(map => map.name === selectedCard);
  //   if (selectedMapping) {
  //     navigate(`/gl-sync/edit`, {
  //       state: {
  //         mappingInstanceId: selectedMapping.id,
  //         sourceGL: selectedMapping.source,
  //         destinationGL: selectedMapping.destination,
  //         systems: [selectedMapping.source, selectedMapping.destination],
  //         tenantId: localStorage.getItem('tenant_id')
  //       }
  //     });
  //   }
  //   handleCardMenuClose();
  // };
  const handleEditCard = () => {
    const selectedMapping = quickMapsData.find(map => map.id === selectedCard); // Find by ID instead of name
    if (selectedMapping) {
      navigate(`/gl-sync/edit`, {
        state: {
          mappingInstanceId: selectedMapping.id,
          sourceGL: selectedMapping.source,
          destinationGL: selectedMapping.destination,
          fundId: selectedMapping.fundId || null,
          fundCode: selectedMapping.fundCode || null,
          fundName: selectedMapping.fundName || null,
          systems: [selectedMapping.source, selectedMapping.destination],
          tenantId: localStorage.getItem('tenant_id')
        }
      });
    }
    handleCardMenuClose();
  };

  const handleDeleteCard = () => {
    const selectedMapping = quickMapsData.find((map) => map.id === selectedCard);
    if (!selectedMapping) {
      setError('No mapping found to delete');
      return;
    }

    setDeleteAction('card');
    setItemToDelete(selectedMapping);
    setConfirmDialogOpen(true);
    handleCardMenuClose();
  };

  const handleDeleteUpload = () => {
    if (!selectedUploadRow) {
      setError('No upload record found to delete');
      return;
    }

    setDeleteAction('upload');
    setItemToDelete(selectedUploadRow);
    setConfirmDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration')?.trim();
      const tenant_id = localStorage.getItem('tenant_id')?.trim();
      const userId = localStorage.getItem('user_id');
      const username = localStorage.getItem('username');

      if (!token || !tenantId) {
        setError('Authentication token or tenant ID not found');
        return;
      }

      if (deleteAction === 'card') {
        // Delete quick mapping card: mapping header → conductor gl-account-mappings → FastAPI gl-link-mappings

        // First API: Delete GL sync mapping headers using mapping instance ID
        const response1 = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/mapping-headers/${itemToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response1.ok) {
          throw new Error('Failed to delete GL sync mapping');
        }

        const result1 = await response1.json();
        console.log('First API (conductor) delete result:', result1);

        const tenantIdForConductor = localStorage.getItem('tenant_id')?.trim();
        if (!tenantIdForConductor) {
          throw new Error('Tenant ID not found. Please ensure you are properly authenticated.');
        }
        const fromGlSourceNorm = itemToDelete.source.endsWith('_GL')
          ? itemToDelete.source
          : `${itemToDelete.source}_GL`;
        const toGlSourceNorm = itemToDelete.destination.endsWith('_GL')
          ? itemToDelete.destination
          : `${itemToDelete.destination}_GL`;

        const conductorAccountMappingsDeleteUrl = new URL(
          `${process.env.REACT_APP_BACKEND_URL}conductor/gl-account-mappings/by-sources`
        );
        conductorAccountMappingsDeleteUrl.searchParams.set('tenant_id', tenantIdForConductor);
        conductorAccountMappingsDeleteUrl.searchParams.set('from_gl_source', fromGlSourceNorm);
        conductorAccountMappingsDeleteUrl.searchParams.set('to_gl_source', toGlSourceNorm);
        conductorAccountMappingsDeleteUrl.searchParams.set('fund_scoped', 'true');
        if (itemToDelete.fundCode) {
          conductorAccountMappingsDeleteUrl.searchParams.set('fund_code', itemToDelete.fundCode);
        }

        const responseAccountMappings = await fetch(conductorAccountMappingsDeleteUrl.toString(), {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!responseAccountMappings.ok) {
          const amText = await responseAccountMappings.text();
          let amErr = {};
          try {
            amErr = amText ? JSON.parse(amText) : {};
          } catch {
            amErr = {};
          }
          throw new Error(
            amErr.message ||
              amErr.error ||
              amErr.detail ||
              `Failed to delete GL account mappings: ${responseAccountMappings.status}`
          );
        }

        const accountMappingsDeleteResult = await responseAccountMappings.json().catch(() => ({}));
        console.log('Conductor gl-account-mappings/by-sources delete result:', accountMappingsDeleteResult);

        // Third API: Delete GL link mappings by sources (fastapi)
        // const response2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/by-sources/?from_gl_source=${itemToDelete.source}&to_gl_source=${itemToDelete.destination}&tenant=${tenantId}`, {
        //   method: 'DELETE',
        //   headers: {
        //     'Authorization': `Bearer ${token}`,
        //     'Content-Type': 'application/json',
        //   },
        // });
        const response2 = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/by-sources/` +
          `?from_gl_source=${encodeURIComponent(itemToDelete.source.endsWith('_GL') ? itemToDelete.source : `${itemToDelete.source}_GL`)}` +
          `&to_gl_source=${encodeURIComponent(itemToDelete.destination.endsWith('_GL') ? itemToDelete.destination : `${itemToDelete.destination}_GL`)}` +
          `&tenant=${encodeURIComponent(tenantId)}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const response2Text = await response2.text();
        let result2 = {};
        try {
          result2 = response2Text ? JSON.parse(response2Text) : {};
        } catch {
          result2 = {};
        }

        const fastApiDetail =
          typeof result2.detail === 'string'
            ? result2.detail
            : Array.isArray(result2.detail)
              ? result2.detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join('; ')
              : response2Text || '';

        if (!response2.ok) {
          // Backend returns a clear message when there are no link rows; treat as idempotent success
          const noMappingsToDelete = /no mappings found/i.test(fastApiDetail);
          if (noMappingsToDelete) {
            console.warn('FastAPI gl-link-mappings delete: none to remove:', fastApiDetail);
          } else {
            throw new Error(fastApiDetail || 'Failed to delete GL link mappings');
          }
        } else {
          console.log('Second API (fastapi) delete result:', result2);
        }

        // Remove the deleted mapping from the local state
        // Log the GL Mapping Workflow deletion
        try {
          await AuditLoggers.glMappingWorkflowDeleted(
            itemToDelete.source || 'Unknown',
            itemToDelete.destination || 'Unknown',
            itemToDelete.id,
            {
              timestamp: new Date().toISOString(),
              userId: localStorage.getItem('user_id'),
              username: localStorage.getItem('username'),
              tenantId: localStorage.getItem('tenant_id'),
              action: 'GL Mapping Workflow Deleted'
            }
          );
        } catch (logError) {
          console.error('Error logging GL Mapping Workflow deletion:', logError);
          // Don't block the operation if logging fails
        }

        const updatedMappings = glMappings.filter(
          (mapping) => mapping.mappingInstanceId !== itemToDelete.id
        );
        setGlMappings(updatedMappings);

        console.log('Both mappings deleted successfully');

      } else if (deleteAction === 'upload') {
        const fileInstanceId = itemToDelete.fileInstanceId ?? itemToDelete.id;
        if (!fileInstanceId) {
          setError('No file instance is linked to this row.');
          return;
        }
        const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/delete`;
        console.log('Calling delete API at URL:', deleteUrl);

        const response = await fetch(deleteUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete upload record');
        }

        // Log the file deletion
        try {
          await AuditLoggers.glSyncFileDeleted(
            itemToDelete.name || 'Unknown File',
            itemToDelete.id,
            itemToDelete.source || 'Unknown',
            {
              timestamp: new Date().toISOString(),
              userId: userId,
              username: username,
              tenantId: tenant_id,
              fileType: itemToDelete.type || 'Unknown',
              action: 'File Deleted'
            }
          );
        } catch (logError) {
          console.error('Error logging file deletion:', logError);
          // Don't block the operation if logging fails
        }

        // Remove the deleted upload from the local state
        const updatedUploads = recentUploads.filter(upload => upload.id !== itemToDelete.id);
        setRecentUploads(updatedUploads);

        console.log('Upload record deleted successfully');
      }

      setConfirmDialogOpen(false);
      setDeleteAction(null);
      setItemToDelete(null);
      setError(null);

    } catch (error) {
      console.error('Error deleting item:', error);
      setError(`Failed to delete ${deleteAction === 'card' ? 'mapping' : 'upload record'}. Please try again.`);
    }
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
    setDeleteAction(null);
    setItemToDelete(null);
  };
  const handleRowClick = async (params) => {
    console.log('Row clicked:', params.row);

    try {
      const token = localStorage.getItem('token');

      // Fetch workflow ID using unitOfWorkId
      const workflowResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${params.row.unitOfWorkId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!workflowResponse.ok) {
        throw new Error('Failed to fetch workflow instance');
      }

      const workflowId = await workflowResponse.json();
      console.log('Workflow ID fetched:', workflowId);

      // Navigate to Trial Balance Compare with stepper
      navigate('/gl-sync/trial-balance-compare', {
        state: {
          uploadData: params.row,
          sourceGL: params.row.source,
          destinationGL: params.row.destination,
          glSystem: stripGlSystemLabel(params.row.source) || params.row.source,
          documentType: params.row.documentType,
          workflowId: workflowId,
          activeStep: 0,
          fundId: params.row.fundId || null,
          fundCode: params.row.fundCode || null,
          fundName: params.row.fundName || null,
        }
      });
    } catch (error) {
      console.error('Error fetching workflow ID:', error);
      setError('Failed to load workflow data. Please try again.');
    }
  };

  // Handle 3-dot menu
  const handleMenuOpen = (event, row) => {
    event.stopPropagation(); // Prevent row click
    setMenuAnchorEl(event.currentTarget);
    setSelectedUploadRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUploadRow(null);
  };

  const handleMappedData = () => {
    console.log('Mapped Data clicked for:', selectedUploadRow);
    // TODO: Implement mapped data functionality
    handleMenuClose();
  };

  const handleOutput = () => {
    console.log('Output clicked for:', selectedUploadRow);
    // TODO: Implement output functionality
    handleMenuClose();
  };

  const handleView = async () => {
    console.log('View clicked for:', selectedUploadRow);
    if (!selectedUploadRow) {
      handleMenuClose();
      return;
    }

    setViewerLoading(true);
    setViewerOpen(true);
    handleMenuClose();

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration')?.trim();

      // First, get the workflow ID using the unitOfWorkId
      const workflowResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${selectedUploadRow.unitOfWorkId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!workflowResponse.ok) {
        throw new Error('Failed to fetch workflow instance');
      }

      const workflowData = await workflowResponse.json();
      const workflowId = workflowData; // The response is the workflow ID directly

      // Now fetch the file data using the landing-internal-cas-ref API
      const fileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/gl-sync/?file_id=${selectedUploadRow.integrationId}&workflow_id=${workflowId}&tenant=${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!fileResponse.ok) {
        throw new Error('Failed to fetch file data');
      }

      const fileData = await fileResponse.json();

      // Keep the original structure but parse any JSON fields for display
      const transformedData = {
        data: fileData.map(item => {
          const transformedItem = { ...item };

          // Parse content field if it exists and is a JSON string
          if (item.content && typeof item.content === 'string') {
            try {
              transformedItem.content = JSON.parse(item.content);
            } catch (error) {
              console.error('Error parsing content field:', error);
            }
          }

          // Parse any other fields that might contain JSON strings
          Object.keys(transformedItem).forEach(key => {
            const value = transformedItem[key];
            if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
              try {
                transformedItem[key] = JSON.parse(value);
              } catch (error) {
                // If parsing fails, keep the original string value
              }
            }
          });

          return transformedItem;
        })
      };

      setViewerData(transformedData);
    } catch (error) {
      console.error('Error fetching file data:', error);
      setError('Failed to load file data: ' + error.message);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewerData(null);
  };

  const handleViewData = async () => {
    console.log('View Data clicked for:', selectedUploadRow);
    if (!selectedUploadRow) {
      handleMenuClose();
      return;
    }

    setViewDataLoading(true);
    setViewDataOpen(true);
    handleMenuClose();

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration')?.trim();

      // First, get the workflow ID using the unitOfWorkId
      const workflowResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${selectedUploadRow.unitOfWorkId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!workflowResponse.ok) {
        throw new Error('Failed to fetch workflow instance');
      }

      const workflowData = await workflowResponse.json();
      const workflowId = workflowData; // The response is the workflow ID directly

      // Now fetch the file data using the landing-internal-cas-ref API
      const fileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/gl-sync/?file_id=${selectedUploadRow.integrationId}&workflow_id=${workflowId}&tenant=${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!fileResponse.ok) {
        throw new Error('Failed to fetch file data');
      }

      const fileData = await fileResponse.json();

      // Extract only JSON content fields for View Data
      const jsonDataOnly = fileData
        .map(item => {
          const jsonFields = {};

          // Extract content field if it exists and is JSON
          if (item.content && typeof item.content === 'string') {
            try {
              jsonFields.content = JSON.parse(item.content);
            } catch (error) {
              console.error('Error parsing content field:', error);
            }
          }

          // Extract any other fields that contain JSON objects
          Object.keys(item).forEach(key => {
            const value = item[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              jsonFields[key] = value;
            } else if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
              try {
                jsonFields[key] = JSON.parse(value);
              } catch (error) {
                // If parsing fails, skip this field
              }
            }
          });

          return Object.keys(jsonFields).length > 0 ? jsonFields : null;
        })
        .filter(item => item !== null);

      setViewDataData({ data: jsonDataOnly });
    } catch (error) {
      console.error('Error fetching file data:', error);
      setError('Failed to load file data: ' + error.message);
    } finally {
      setViewDataLoading(false);
    }
  };

  const handleCloseViewData = () => {
    setViewDataOpen(false);
    setViewDataData(null);
  };

  return (
    <Box sx={{ ...harmonatePageSx, p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        {/* <Typography color="text.primary">GL Sync</Typography> */}
      </Breadcrumbs>

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* GL Mapping Workflows Section */}
      {/* GL Mapping Workflows Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={harmonatePageTitleSx}>
          GL Mapping Workflows
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Navigation arrows - only show if there are more than 4 cards */}
          {quickMapsData.length > cardsPerPage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handlePreviousCards}
                disabled={currentCardPage === 0}
                sx={{
                  backgroundColor: currentCardPage === 0 ? ht.listBg : ht.secondaryTab,
                  '&:hover': { backgroundColor: currentCardPage === 0 ? ht.listBg : ht.secondaryTab },
                  '&:disabled': { color: ht.fadedText }
                }}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography variant="body2" sx={{ mx: 1, color: ht.white, fontSize: '12px' }}>
                {currentCardPage + 1} of {totalPages}
              </Typography>
              <IconButton
                onClick={handleNextCards}
                disabled={currentCardPage >= totalPages - 1}
                sx={{
                  backgroundColor: currentCardPage >= totalPages - 1 ? ht.listBg : ht.secondaryTab,
                  '&:hover': { backgroundColor: currentCardPage >= totalPages - 1 ? ht.listBg : ht.secondaryTab },
                  '&:disabled': { color: ht.fadedText }
                }}
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </Box>
          )}

          {/* 3-dot menu - moved to the end */}
          <IconButton
            onClick={handleWorkflowMenuOpen}
            size="small"
            sx={harmonatePlainIconButtonSx}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Menu
          anchorEl={workflowMenuAnchor}
          open={Boolean(workflowMenuAnchor)}
          onClose={handleWorkflowMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { borderRadius: ht.fieldRadius }
          }}
        >
          <MenuItem onClick={handleAccountsClick}>
            Accounts
          </MenuItem>
        </Menu>
      </Box>


      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '120px' }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          </Grid>
        ) : currentCards.length > 0 ? (
          currentCards.map((map) => (
            <Grid item xs={12} sm={6} md={3} key={map.id}>  {/* Changed from key={map.name} to key={map.id} */}
              <Tooltip
                enterDelay={280}
                title={
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.65,
                      maxWidth: 220,
                      py: 0.15,
                    }}
                  >
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                          opacity: 0.72,
                          mb: 0.2,
                        }}
                      >
                        Fund
                      </Typography>
                      <Typography
                        component="div"
                        sx={{ fontSize: '11px', lineHeight: 1.35, wordBreak: 'break-word', fontWeight: 400 }}
                      >
                        {map.fundLabel || 'All funds'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                          opacity: 0.72,
                          mb: 0.2,
                        }}
                      >
                        Description
                      </Typography>
                      <Typography
                        component="div"
                        sx={{ fontSize: '11px', lineHeight: 1.35, wordBreak: 'break-word', fontWeight: 400 }}
                      >
                        {map.mappingDescription || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                          opacity: 0.72,
                          mb: 0.2,
                        }}
                      >
                        Template
                      </Typography>
                      <Typography
                        component="div"
                        sx={{ fontSize: '11px', lineHeight: 1.35, wordBreak: 'break-word', fontWeight: 400 }}
                      >
                        {map.templateName || '—'}
                      </Typography>
                    </Box>
                  </Box>
                }
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 236,
                      px: 1,
                      py: 0.65,
                    },
                  },
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '120px',
                      borderRadius: ht.cardRadius,
                      bgcolor: ht.white,
                      cursor: 'pointer',
                      boxShadow: ht.cardShadow,
                      position: 'relative',
                      '&:hover': {
                        boxShadow: ht.cardShadow
                      }
                    }}
                    onClick={() => handleCardClick(map)}
                  >
                    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
                            {map.fundLabel ? `${map.source} · ${map.fundLabel}` : map.source}
                          </Typography>
                        </Box>
                        <Box sx={{ backgroundColor: ht.secondaryTab, borderRadius: ht.fieldRadius, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PeopleIcon sx={{ color: ht.text, fontSize: 20 }} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '14px' }}>
                          {map.name}
                        </Typography>
                        {/* <IconButton
                      size="small"
                      onClick={(e) => handleCardMenuClick(e, map.name)}
                      sx={{ color: '#000000', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)', color: '#000000' } }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton> */}
                        <IconButton
                          size="small"
                          onClick={(e) => handleCardMenuClick(e, map.id)}  // Changed from map.name to map.id
                          sx={harmonateGridActionIconSx}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Tooltip>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ ...harmonateCardSx, p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No GL mappings found. Create your first mapping to get started.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Card Menu Dropdown */}
      <Menu
        anchorEl={cardMenuAnchorEl}
        open={Boolean(cardMenuAnchorEl)}
        onClose={handleCardMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: ht.cardShadow,
            borderRadius: ht.fieldRadius,
            minWidth: '120px'
          }
        }}
      >
        <MenuItem
          onClick={handleEditCard}
          disabled={!isHarmonateUser && !hasPermission('GL Sync', 'edit')}
          sx={{
            fontSize: '14px',
            py: 1,
            px: 2,
            '&:hover': {
              bgcolor: ht.listBg
            }
          }}
        >
          {/* <EditIcon sx={{ mr: 1, fontSize: '18px' }} />
          Edit */}
          <EditIcon sx={{
            mr: 1,
            fontSize: '18px',
            color: (!isHarmonateUser && !hasPermission('GL Sync', 'edit')) ? 'rgba(0, 0, 0, 0.26)' : 'inherit'
          }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={handleDeleteCard}
          disabled={!isHarmonateUser && !hasPermission('GL Sync', 'delete')}
          sx={{
            fontSize: '14px',
            py: 1,
            px: 2,
            color: ht.red,
            '&:hover': {
              bgcolor: 'rgba(239, 63, 63, 0.1)'
            },
            '&.Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.26)',
              '& .MuiSvgIcon-root': {
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: '18px' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Recent Uploads Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'medium', fontSize: '18px', color: ht.white }}>
          Uploads
        </Typography>
        <IconButton
          size="small"
          aria-label="Uploads options"
          onClick={(e) => setUploadsHeaderMenuAnchor(e.currentTarget)}
          sx={{ color: ht.white, ...harmonatePlainIconButtonSx }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <Menu
        anchorEl={uploadsHeaderMenuAnchor}
        open={Boolean(uploadsHeaderMenuAnchor)}
        onClose={() => setUploadsHeaderMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: ht.fieldRadius,
            boxShadow: ht.cardShadow,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setShowSubDocs((prev) => !prev);
            setUploadsHeaderMenuAnchor(null);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <Typography variant="body2">
            {showSubDocs ? 'Hide sub docs' : 'View sub docs'}
          </Typography>
        </MenuItem>
      </Menu>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, mb: 3 }}>
        {loadingUploads ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Loading recent uploads...
              </Typography>
            </Box>
          </Box>
        ) : (
          <DataGridPro
          columnResizeMode="onEnd"
            resizable
            rows={visibleUploads}
            columns={columns}
            rowHeight={40}
            onRowClick={handleRowClick}
            sx={{
              ...harmonateDataGridSx,
              '& .MuiDataGrid-row:hover': {
                bgcolor: ht.listBg,
                cursor: 'pointer'
              }
            }}
            disableSelectionOnClick
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            noRowsOverlay={() => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No recent uploads available
                </Typography>
              </Box>
            )}
          />
        )}
      </Paper>

      {/* Action Buttons and Quarter Selection */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={!isHarmonateUser && !hasPermission('GL Sync', 'add')}
            sx={harmonateProceedButtonSx}
          >
            New Mapping
          </Button>
          {/* <Button
            variant="contained"
            onClick={handleFileUploadClick}
            sx={{
              bgcolor: '#8CD867',
              '&:hover': { bgcolor: '#7BC857' },
              borderRadius: 28,
              px: 3,
              py: 1,
              fontSize: '14px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            Saved Mappings
          </Button> */}
        </Box>

        {/* <Button
          variant="contained"
          endIcon={<KeyboardArrowUpIcon />}
          sx={{
            bgcolor: '#f0f0f0',
            color: '#000000',
            '&:hover': { bgcolor: '#e0e0e0' },
            borderRadius: 28,
            px: 3,
            py: 1,
            fontSize: '14px',
            textTransform: 'none',
            boxShadow: 'none',
            fontWeight: 400
          }}
        >
          Q2 2025
        </Button> */}
      </Box>

      {/* 3-dot Menu for Recent Uploads */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: ht.fieldRadius,
            boxShadow: ht.cardShadow,
            minWidth: 220,
            maxWidth: 320,
          }
        }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Surrogate key
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-word' }}>
            {selectedUploadRow?.surrogateKey && selectedUploadRow.surrogateKey !== '—'
              ? selectedUploadRow.surrogateKey
              : '—'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleView}>
          <Typography variant="body2">View Metadata</Typography>
        </MenuItem>
        <MenuItem onClick={handleViewData}>
          <Typography variant="body2">View Data</Typography>
        </MenuItem>
        {/* <MenuItem onClick={handleMappedData}>
          <Typography variant="body2">Mapped Data</Typography>
        </MenuItem> */}
        {/* <MenuItem onClick={handleOutput}>
          <Typography variant="body2">Output</Typography>
        </MenuItem> */}
        {/* <MenuItem onClick={handleDeleteUpload}
          disabled={!isHarmonateUser && !hasPermission('GL Sync', 'delete')}
          sx={{
            color: '#f44336',
            '&.Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.26)'
            }
          }}
        >
          <Typography variant="body2">Delete</Typography>
        </MenuItem> */}
      </Menu>

      {/* Document Details & Audit Logs — same pattern as WorkflowDrawer / BankReference */}
      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => {
            setIsLogsModalOpen(false);
            setDocumentDetails(null);
            setAuditLogs([]);
            setLogsModalSourceRow(null);
            setLogsJsonDialogOpen(false);
            setLogsJsonData(null);
            setLogsJsonError(null);
            setLogsExtractionDialogOpen(false);
            setLogsExtractionDetails(null);
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: '95%',
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              ...harmonateCardSx,
              bgcolor: ht.white,
            }}
          >
            <Box
              sx={{
                ...harmonateDialogTitleSx,
                p: 2,
                borderBottom: 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, flexGrow: 1, color: ht.white }}>
                  Document Details & Audit Logs
                </Typography>
                {/* <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLogsModalSnowflakeClick();
                  }}
                  disabled={
                    logsExtractionLoading ||
                    !logsModalSourceRow?.fastApiFileInstanceId ||
                    !logsModalSourceRow?.processId
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                    minWidth: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {logsExtractionLoading ? <CircularProgress size={16} /> : 'Snowflake Info'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (logsModalSourceRow) {
                      handleLogsModalViewRawJson(logsModalSourceRow);
                    }
                  }}
                  disabled={
                    !logsModalSourceRow?.unitOfWorkId ||
                    !logsModalSourceRow?.fastApiFileInstanceId ||
                    !logsModalSourceRow?.processId
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                  }}
                >
                  Raw JSON
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    // eslint-disable-next-line no-console
                    console.log('Parser Status clicked');
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                  }}
                >
                  Parser Status
                </Button> */}
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsLogsModalOpen(false);
                    setDocumentDetails(null);
                    setAuditLogs([]);
                    setLogsModalSourceRow(null);
                    setLogsJsonDialogOpen(false);
                    setLogsJsonData(null);
                    setLogsJsonError(null);
                    setLogsExtractionDialogOpen(false);
                    setLogsExtractionDetails(null);
                  }}
                  sx={harmonateDialogCloseIconSx}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.green }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                {documentDetails && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: ht.white,
                      borderRadius: ht.cardRadius,
                      border: `1px solid ${ht.line}`,
                      boxShadow: ht.cardShadow,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: 1.5,
                        pb: 1,
                        borderBottom: `1px solid ${ht.line}`,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                        gap: 3,
                        rowGap: 2.5,
                        '& > div': {
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                        },
                        mb: 0,
                      }}
                    >
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                          Document Name
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text, fontWeight: 500 }}>
                          {documentDetails.name || 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                          Document Type
                        </Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.documentTypeName || documentDetails.documentTypeId || 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                          Uploaded At
                        </Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.uploadAt || documentDetails.createdAt
                            ? formatDateTime(
                                documentDetails.uploadAt || documentDetails.createdAt,
                                dateTimeFormat
                              )
                            : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                          Location
                        </Typography>
                        <Tooltip title={documentDetails.location || 'N/A'} arrow placement="top">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: '300px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              display: 'inline-block',
                              verticalAlign: 'bottom',
                              fontSize: '0.875rem',
                              color: ht.text,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationColor: ht.text,
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '2px',
                              },
                            }}
                          >
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                          Unit of Work
                        </Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.unitOfWorkId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                          Integration ID
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>
                          {documentDetails.integrationId != null ? String(documentDetails.integrationId) : 'N/A'}
                        </Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${ht.line}`,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                      AUDIT LOGS
                    </Typography>
                  </Box>
                  {auditLogs.length > 0 ? (
                    <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                      <DataGridPro
                        rows={auditLogs}
                        pagination
                        paginationModel={auditLogsPaginationModel}
                        onPaginationModelChange={setAuditLogsPaginationModel}
                        pageSizeOptions={[10, 25, 50]}
                        columns={[
                          {
                            field: 'createdAt',
                            headerName: 'Timestamp',
                            flex: 1,
                            minWidth: 160,
                            resizable: true,
                            valueFormatter: (params) =>
                              params?.value ? new Date(params.value).toLocaleString() : 'N/A',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Timestamp
                              </Typography>
                            ),
                          },
                          {
                            field: 'service',
                            headerName: 'Service',
                            flex: 0.8,
                            minWidth: 150,
                            resizable: true,
                            cellClassName: 'service-cell',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Service
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box
                                sx={{
                                  bgcolor:
                                    params.value === 'upload'
                                      ? ht.secondaryTab
                                      : params.value === 'document_classification'
                                        ? 'rgba(2, 107, 29, 0.12)'
                                        : params.value === 'extraction'
                                          ? 'rgba(46, 166, 160, 0.18)'
                                          : params.value === 'ingest'
                                            ? 'rgba(1, 48, 98, 0.12)'
                                            : ht.listBg,
                                  color:
                                    params.value === 'upload'
                                      ? ht.text
                                      : params.value === 'document_classification'
                                        ? ht.green
                                        : params.value === 'extraction'
                                          ? ht.blue
                                          : params.value === 'ingest'
                                            ? ht.text
                                            : ht.text,
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: ht.buttonRadius,
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  textTransform: 'none',
                                  minWidth: 'fit-content',
                                  width: 'auto',
                                  whiteSpace: 'nowrap',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {params.value || 'N/A'}
                              </Box>
                            ),
                          },
                          {
                            field: 'action',
                            headerName: 'Action',
                            flex: 1,
                            minWidth: 120,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Action
                              </Typography>
                            ),
                            valueFormatter: (params) => params?.value || 'N/A',
                            renderCell: (params) => (
                              <Box
                                sx={{
                                  color: ht.text,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  textTransform: 'none',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {params.formattedValue != null && params.formattedValue !== ''
                                  ? params.formattedValue
                                  : params.value || 'N/A'}
                              </Box>
                            ),
                          },
                          {
                            field: 'status',
                            headerName: 'Status',
                            flex: 0.8,
                            minWidth: 100,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Status
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box
                                sx={{
                                  color: ht.text,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {params.value || 'N/A'}
                              </Box>
                            ),
                          },
                          {
                            field: 'details',
                            headerName: 'Details',
                            flex: 2,
                            minWidth: 200,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Details
                              </Typography>
                            ),
                            renderCell: (params) => {
                              const displayText =
                                params.value == null
                                  ? ''
                                  : typeof params.value === 'object'
                                    ? JSON.stringify(params.value)
                                    : String(params.value);
                              const preview =
                                displayText.length > 0
                                  ? `${displayText.slice(0, 50)}${displayText.length > 50 ? '...' : ''}`
                                  : 'N/A';
                              return (
                                <Tooltip
                                  title={
                                    params.value
                                      ? typeof params.value === 'object'
                                        ? JSON.stringify(params.value, null, 2)
                                        : String(params.value)
                                      : 'N/A'
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    sx={{
                                      width: '100%',
                                      '&:hover': {
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        textDecorationColor: ht.text,
                                        textDecorationThickness: '1px',
                                        textUnderlineOffset: '2px',
                                      },
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      noWrap
                                      sx={{
                                        color: ht.text,
                                        fontSize: '0.8rem',
                                        fontFamily: 'monospace',
                                      }}
                                    >
                                      {preview}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            },
                          },
                        ]}
                        getRowId={(row) =>
                          row?.id != null
                            ? String(row.id)
                            : `${row?.createdAt ?? ''}_${row?.service ?? ''}_${row?.action ?? ''}_${row?.status ?? ''}`
                        }
                        disableRowSelectionOnClick
                        disableColumnMenu
                        hideFooterSelectedRowCount
                        sx={{
                          ...harmonateDataGridSx,
                          border: 'none',
                          '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: ht.listBg,
                            borderBottom: `1px solid ${ht.line}`,
                            minHeight: '40px !important',
                            maxHeight: '40px !important',
                          },
                          '& .MuiDataGrid-columnHeader': {
                            py: 1,
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            color: ht.text,
                          },
                          '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            color: ht.text,
                            py: 1,
                            borderColor: ht.line,
                          },
                          '& .MuiDataGrid-row': {
                            '&:hover': {
                              backgroundColor: ht.secondaryTab,
                            },
                            '&.Mui-selected': {
                              backgroundColor: ht.secondaryTab,
                              '&:hover': {
                                backgroundColor: ht.secondaryTab,
                              },
                            },
                          },
                          '& .MuiDataGrid-footerContainer': {
                            borderTop: `1px solid ${ht.line}`,
                            minHeight: '48px',
                          },
                        }}
                        components={{
                          LoadingOverlay: LinearProgress,
                          NoRowsOverlay: () => (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: ht.text }}>
                                No audit logs available
                              </Typography>
                            </Box>
                          ),
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography sx={{ color: ht.text }}>No audit logs available</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* WorkflowDrawer-parity: extraction JSON (Snowflake Info) */}
      <Dialog
        open={logsExtractionDialogOpen}
        onClose={() => setLogsExtractionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Typography variant="h6" sx={{ color: ht.white }}>Extraction Details</Typography>
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {logsExtractionLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : logsExtractionDetails ? (
            <pre
              style={{
                backgroundColor: ht.listBg,
                padding: '16px',
                borderRadius: '4px',
                maxHeight: '60vh',
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                margin: 0,
              }}
            >
              {JSON.stringify(logsExtractionDetails, null, 2)}
            </pre>
          ) : (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">No extraction data available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button
            onClick={() => setLogsExtractionDialogOpen(false)}
            variant="contained"
            sx={harmonateProceedButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* WorkflowDrawer-parity: Raw JSON file content */}
      <Dialog
        open={logsJsonDialogOpen}
        onClose={() => setLogsJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>File Content</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {logsJsonLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : logsJsonError ? (
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {logsJsonError}
            </Alert>
          ) : logsJsonData && logsJsonData.isBinary ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Binary File Detected
              </Typography>
              <Typography variant="body1" paragraph>
                This is a binary file and cannot be displayed directly.
              </Typography>
              <Button
                variant="contained"
                component="a"
                href={logsJsonData.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ...harmonateProceedButtonSx, mt: 2 }}
              >
                Download File
              </Button>
            </Box>
          ) : (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontFamily: 'monospace',
                maxHeight: '60vh',
                overflow: 'auto',
                padding: '16px',
                backgroundColor: ht.listBg,
                borderRadius: '4px',
              }}
            >
              {logsJsonData?.content || 'No data available'}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          {logsJsonData?.downloadUrl && (
            <Button
              component="a"
              href={logsJsonData.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={harmonateCancelButtonSx}
            >
              Download
            </Button>
          )}
          <Button onClick={() => setLogsJsonDialogOpen(false)} sx={harmonateCancelButtonSx}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Excel Viewer Dialog */}
      <ExcelViewerDialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        data={viewerData}
        loading={viewerLoading}
        error={error}
        fileName={selectedUploadRow?.name}
      />

      {/* View Data Dialog */}
      <ViewDataDialog
        open={viewDataOpen}
        onClose={handleCloseViewData}
        data={viewDataData}
        loading={viewDataLoading}
        error={error}
        fileName={selectedUploadRow?.name}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Confirm Delete
        </DialogTitle>

        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body1">
            {deleteAction === 'card'
              ? `Are you sure you want to delete the mapping "${itemToDelete?.name}"? This action cannot be undone.`
              : `Are you sure you want to delete the upload record "${itemToDelete?.name}"? This action cannot be undone.`
            }
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={cancelDelete}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GLSync;